// FIXME: This should be exported from the ra-core package
type CanAccessParams<
  RecordType extends Record<string, any> = Record<string, any>,
> = {
  action: string;
  resource: string;
  record?: RecordType;
};

/**
 * CRM roles, highest first. They mirror public.member_roles: crm = "admin"
 * makes an administrator, superadmin = true a superadmin. The database
 * enforces the same rules through RLS and the users edge function; this
 * matrix only decides what the UI offers, including direct routes such as
 * /sales/create (react-admin checks canAccess on every resource page).
 */
export type CrmRole = "superadmin" | "admin" | "user";

export const getCrmRole = (sale: {
  administrator?: boolean;
  superadmin?: boolean;
}): CrmRole =>
  sale.superadmin === true
    ? "superadmin"
    : sale.administrator
      ? "admin"
      : "user";

const ALL = ["list", "show", "create", "edit", "delete", "export"] as const;
type Action = (typeof ALL)[number];

// Shared data (Martin, 18.09.2026): contacts and companies are shared across
// Fraktionen, and whoever may create may also delete. Notes are filtered by
// Fraktion in the database, not here.
const SHARED: Action[] = [...ALL];

const MATRIX: Record<CrmRole, Record<string, readonly Action[]>> = {
  superadmin: { "*": ALL },
  // Fraktions-Admins manage members of their own Fraktion (RLS and the users
  // function enforce it); the app configuration is for superadmins only.
  admin: { "*": ALL, configuration: [] },
  user: {
    "*": SHARED,
    // Members and the app configuration are managed by administrators;
    // everybody edits their own profile under /settings.
    sales: [],
    configuration: [],
  },
};

export const canAccess = <
  RecordType extends Record<string, any> = Record<string, any>,
>(
  role: string,
  params: CanAccessParams<RecordType>,
) => {
  const rules = MATRIX[role as CrmRole] ?? MATRIX.user;
  const allowed = rules[params.resource] ?? rules["*"];
  return (allowed as readonly string[]).includes(params.action);
};
